// The overlay layer of the UI shell (M11, absorbed by M7 — ui/App.tsx describes the whole layer model): a
// standalone Preact root (`#overlay`, z 200, above every layer incl. the splash) for the ErrorScreen,
// ContextLostOverlay, ElsewhereOverlay, the M7 RotateOverlay and the one-time A2HS card (one flag,
// meta.uiFlags.a2hs). It is its own root because the ErrorScreen must work at any point of the boot (before
// the data and the shell too); it falls back to the plain-DOM screen of platform/support.ts if Preact itself
// fails. overlays.css keeps its own constant colours (--ov-*): they work before the palette tokens exist.
import { render } from 'preact';
import type { CopyData } from '../../data/schema.ts';
import type { OverlaySink } from '../../game/lifecycle.ts';
import type { A2hsKind } from '../../platform/a2hs.ts';
import { showErrorScreen as showDomErrorScreen } from '../../platform/support.ts';
import { A2hsHint } from './A2hsHint.tsx';
import { ContextLostOverlay } from './ContextLostOverlay.tsx';
import { ElsewhereOverlay } from './ElsewhereOverlay.tsx';
import { ErrorScreen } from './ErrorScreen.tsx';
import { RotateOverlay } from './RotateOverlay.tsx';
import { a2hsSig, type BlockingOverlay, blockingSig, rotateSig, showBlocking } from './store.ts';
import './overlays.css';

const ROOT_ID = 'overlay';

function OverlayRoot() {
  const b = blockingSig.value;
  const hint = a2hsSig.value;
  const rotate = rotateSig.value;
  return (
    <>
      {hint !== null && b.k === 'none' && rotate === null && (
        <A2hsHint text={hint.text} button={hint.button} onDismiss={hint.onDismiss} />
      )}
      {rotate !== null && b.k === 'none' && <RotateOverlay text={rotate} />}
      {b.k === 'contextLost' && <ContextLostOverlay text={b.text} onTap={b.onTap} />}
      {b.k === 'elsewhere' && (
        <ElsewhereOverlay text={b.text} button={b.button} onPlayHere={b.onPlayHere} />
      )}
      {b.k === 'error' && (
        <ErrorScreen title={b.title} detail={b.detail} reload={b.reload} onReload={b.onReload} />
      )}
    </>
  );
}

/** Mounts the overlay root once (idempotent). */
export function ensureOverlayRoot(doc: Document = document): HTMLElement {
  const found = doc.getElementById(ROOT_ID);
  if (found) return found;
  const host = doc.createElement('div');
  host.id = ROOT_ID;
  doc.body.appendChild(host);
  render(<OverlayRoot />, host);
  return host;
}

export interface Overlays extends OverlaySink {
  /**
   * Shows the one-time A2HS card: 'ios' = the iOS Safari how-to (overlay.a2hsHint), 'note' = the layout
   * note (overlay.a2hsNote). `onDismiss` runs when the player closes it.
   */
  a2hs(kind: A2hsKind, onDismiss: () => void): void;
}

/** The blocking overlay up now (diagnostics: __gm.lifecycle; also before createOverlays ran). */
export function overlayState(): BlockingOverlay['k'] | 'rotate' {
  const k = blockingSig.value.k;
  return k === 'none' && rotateSig.value !== null ? 'rotate' : k;
}

export function a2hsState(): 'hidden' | 'shown' {
  return a2hsSig.value === null ? 'hidden' : 'shown';
}

export function createOverlays(copy: CopyData, doc: Document = document): Overlays {
  ensureOverlayRoot(doc);
  const o = copy.overlay;
  return {
    contextLost(onTap) {
      showBlocking({ k: 'contextLost', text: o.contextLost ?? '', onTap });
    },
    elsewhere(onPlayHere) {
      showBlocking({
        k: 'elsewhere',
        text: o.elsewhere ?? '',
        button: o.elsewhereButton ?? '',
        onPlayHere,
      });
    },
    a2hs(kind, onDismiss) {
      a2hsSig.value = {
        text: (kind === 'ios' ? o.a2hsHint : o.a2hsNote) ?? '',
        button: o.a2hsDismiss ?? '',
        onDismiss() {
          a2hsSig.value = null;
          onDismiss();
        },
      };
    },
  };
}

/**
 * The ErrorScreen (replaces whatever overlay is up and stays). `reload`: the Reload button's label, or
 * null for no button (an unsupported browser).
 */
export function showErrorScreen(
  title: string,
  detail: string,
  reload: string | null = null,
  doc: Document = document,
): void {
  try {
    ensureOverlayRoot(doc);
    showBlocking({
      k: 'error',
      title,
      detail,
      reload,
      onReload: () => doc.defaultView?.location.reload(),
    });
  } catch {
    showDomErrorScreen(title, detail, doc);
  }
}
